import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import toast from 'react-hot-toast';
import { createClient } from '@supabase/supabase-js';
import RDPHeader from '../Components/Header/RDPHeader';
import RDPFooter from '../Components/Footers/RDPFooter';

// Initialiser Supabase
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

const ContactPage = () => {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    societe: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Préparer les données avec les produits du panier
      const emailData = {
        ...formData,
        products: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          code: item.code,
          capacity: item.capacity,
          category: item.category
        }))
      };

      // Appeler la fonction Supabase Edge
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: emailData
      });

      if (error) throw error;

      toast.success('Message envoyé avec succès !', {
        icon: '✅',
        duration: 4000
      });

      // Réinitialiser le formulaire et vider le panier
      setFormData({
        prenom: '',
        nom: '',
        societe: '',
        email: '',
        message: ''
      });
      clearCart();

    } catch (error) {
      console.error('Erreur lors de l\'envoi:', error);
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.', {
        icon: '❌',
        duration: 4000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <RDPHeader />
      <div className="bg-gradient-to-b from-glass-lightest via-white to-glass-lightest py-[120px] sm:py-[160px]">
      <Container>
        {/* Titre de la page */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-[60px]"
        >
          <h1 className="font-serif font-bold text-glass-darkest text-[42px] sm:text-[52px] mb-[20px] leading-tight">
            Contactez-<span className="text-glass-accent">nous</span>
          </h1>
          <p className="text-glass-dark text-[18px] max-w-[700px] mx-auto leading-relaxed">
            Une question ? Un projet ? Notre équipe est là pour vous accompagner dans tous vos besoins en emballage verre.
          </p>
        </motion.div>

        {/* Panier - Affichage en haut si des produits sont sélectionnés */}
        {cartItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-[40px] bg-gradient-to-r from-glass-accent/10 to-blue-50 border-l-4 border-glass-accent rounded-lg p-[25px]"
          >
            <div className="flex items-center justify-between mb-[20px]">
              <h3 className="font-serif font-semibold text-glass-darkest text-[22px] flex items-center gap-[10px]">
                <i className="line-icon-Basket-Coins text-glass-accent text-[28px]"></i>
                Produits sélectionnés ({cartItems.length})
              </h3>
              <button
                onClick={clearCart}
                className="text-glass-dark hover:text-red-600 text-[14px] font-medium transition-colors flex items-center gap-[6px]"
              >
                <i className="line-icon-Trash text-[16px]"></i>
                Vider le panier
              </button>
            </div>
            <p className="text-glass-dark text-[14px] mb-[20px]">
              Ces produits seront automatiquement inclus dans votre message
            </p>
            <div className="space-y-[12px]">
              {cartItems.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-white rounded-lg p-[12px] shadow-sm border border-glass-light/30 flex items-center gap-[12px] hover:shadow-md transition-shadow"
                >
                  {item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-[60px] h-[60px] sm:w-[70px] sm:h-[70px] object-contain rounded-md flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-glass-darkest text-[14px] sm:text-[15px] mb-[4px] line-clamp-2">
                      {item.name}
                    </h4>
                    <div className="flex flex-wrap gap-x-[8px] gap-y-[2px] text-[12px] text-glass-dark">
                      {item.capacity && (
                        <span className="font-medium">{item.capacity} ml</span>
                      )}
                      {item.code && (
                        <span className="text-glass-dark/70">• Code: {item.code}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-glass-dark hover:text-red-600 transition-colors p-[8px] flex-shrink-0 hover:bg-red-50 rounded-md"
                    aria-label="Retirer du panier"
                  >
                    <i className="line-icon-Close text-[18px]"></i>
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Contenu principal : Formulaire et Informations */}
        <Row className="g-4">
          {/* Colonne gauche : Formulaire */}
          <Col lg={7}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-[35px] sm:p-[45px] border border-glass-light/20 h-full flex flex-col"
            >
              <h2 className="font-serif font-semibold text-glass-darkest text-[28px] mb-[10px]">
                Envoyez-nous un message
              </h2>
              <p className="text-glass-dark text-[15px] mb-[30px]">
                Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.
              </p>

              <form onSubmit={handleSubmit} className="space-y-[20px] flex-1 flex flex-col">
                {/* Prénom et Nom */}
                <Row className="g-3">
                  <Col md={6}>
                    <label className="block text-glass-darkest font-medium text-[14px] mb-[8px]">
                      Prénom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      required
                      className="w-full px-[16px] py-[12px] border border-glass-light/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-glass-accent focus:border-transparent transition-all text-[15px]"
                      placeholder="Jean"
                    />
                  </Col>
                  <Col md={6}>
                    <label className="block text-glass-darkest font-medium text-[14px] mb-[8px]">
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      required
                      className="w-full px-[16px] py-[12px] border border-glass-light/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-glass-accent focus:border-transparent transition-all text-[15px]"
                      placeholder="Dupont"
                    />
                  </Col>
                </Row>

                {/* Société */}
                <div>
                  <label className="block text-glass-darkest font-medium text-[14px] mb-[8px]">
                    Société
                  </label>
                  <input
                    type="text"
                    name="societe"
                    value={formData.societe}
                    onChange={handleChange}
                    className="w-full px-[16px] py-[12px] border border-glass-light/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-glass-accent focus:border-transparent transition-all text-[15px]"
                    placeholder="Votre entreprise"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-glass-darkest font-medium text-[14px] mb-[8px]">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-[16px] py-[12px] border border-glass-light/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-glass-accent focus:border-transparent transition-all text-[15px]"
                    placeholder="jean.dupont@exemple.fr"
                  />
                </div>

                {/* Message */}
                <div className="flex-1 flex flex-col">
                  <label className="block text-glass-darkest font-medium text-[14px] mb-[8px]">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="w-full flex-1 min-h-[180px] px-[16px] py-[12px] border border-glass-light/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-glass-accent focus:border-transparent transition-all text-[15px] resize-none"
                    placeholder="Décrivez votre projet ou votre demande..."
                  />
                </div>

                {/* Bouton submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-glass-accent to-blue-600 hover:from-blue-600 hover:to-glass-accent text-white font-semibold py-[14px] px-[30px] rounded-lg transition-all duration-300 shadow-md hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-[10px] text-[16px]"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-[20px] h-[20px] border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <i className="line-icon-Mail-Send text-[20px]"></i>
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </Col>

          {/* Colonne droite : Informations de contact */}
          <Col lg={5}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="h-full"
            >
              {/* Carte principale d'informations */}
              <div className="bg-white rounded-2xl shadow-lg border border-glass-light p-[35px] sm:p-[45px] h-full flex flex-col">
                <h2 className="font-serif font-semibold text-[28px] mb-[10px] text-glass-darkest">
                  Nos coordonnées
                </h2>
                <p className="text-glass-dark text-[15px] mb-[35px]">
                  Contactez-nous directement pour discuter de vos projets
                </p>

                <div className="space-y-[28px] flex-1">
                  {/* Contact direct */}
                  <div>
                    <h3 className="text-[18px] font-semibold mb-[15px] text-glass-darkest">Pour contacter RDP directement:</h3>

                    {/* Direction Commerciale */}
                    <div className="bg-glass-lightest border border-glass-light rounded-xl p-[20px] mb-[15px]">
                      <p className="font-semibold text-[15px] mb-[12px] text-glass-darkest">Direction Commerciale: Thierry Dehon</p>

                      <div className="space-y-[10px]">
                        {/* Email */}
                        <div className="flex items-center gap-[12px]">
                          <div className="w-[36px] h-[36px] bg-glass-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="line-icon-Mail text-[18px] text-glass-accent"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] text-glass-medium mb-[2px]">Email:</p>
                            <a
                              href="mailto:thierry.dehon@rdp-glass.com"
                              className="text-glass-accent hover:text-glass-darkest transition-colors text-[14px] font-medium break-all"
                            >
                              thierry.dehon@rdp-glass.com
                            </a>
                          </div>
                        </div>

                        {/* Téléphone */}
                        <div className="flex items-center gap-[12px]">
                          <div className="w-[36px] h-[36px] bg-glass-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="line-icon-Phone-2 text-[18px] text-glass-accent"></i>
                          </div>
                          <div className="flex-1">
                            <p className="text-[12px] text-glass-medium mb-[2px]">Tel. :</p>
                            <a
                              href="tel:+33680087847"
                              className="text-glass-accent hover:text-glass-darkest transition-colors text-[14px] font-medium"
                            >
                              +33 6 80 08 78 47
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Adresse */}
                  <div className="bg-glass-lightest border border-glass-light rounded-xl p-[20px]">
                    <div className="flex items-start gap-[12px]">
                      <div className="w-[36px] h-[36px] bg-glass-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="line-icon-Geo2-Love text-[18px] text-glass-accent"></i>
                      </div>
                      <div>
                        <p className="text-[12px] text-glass-medium mb-[4px]">Adresse:</p>
                        <p className="text-glass-darkest text-[14px] leading-relaxed font-medium">
                          2 Rue d'Hem,<br />
                          59170 Croix<br />
                          France
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Réseaux sociaux */}
                <div className="mt-[30px] pt-[25px] border-t border-glass-light">
                  <h4 className="font-semibold text-[16px] mb-[15px] text-glass-darkest">Suivez-nous</h4>
                  <div className="flex gap-[12px]">
                    <a
                      href="https://www.linkedin.com/in/thierry-dehon-77889118/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-[50px] h-[50px] bg-glass-accent/10 hover:bg-glass-accent hover:text-white text-glass-accent rounded-lg flex items-center justify-center transition-all"
                      aria-label="LinkedIn"
                    >
                      <i className="fab fa-linkedin-in text-[22px]"></i>
                    </a>
                    <a
                      href="mailto:thierry.dehon@rdp-glass.com"
                      className="w-[50px] h-[50px] bg-glass-accent/10 hover:bg-glass-accent hover:text-white text-glass-accent rounded-lg flex items-center justify-center transition-all"
                      aria-label="Email"
                    >
                      <i className="fas fa-envelope text-[22px]"></i>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
    <RDPFooter />
    </>
  );
};

export default ContactPage;
